import { Fragment, useContext, useState } from "react";
import { DispatchContext, StateContext } from "../Round";

import { RoundTitle } from "@client/components/round/RoundTitle";
import { Button } from "@client/components/utils/Button";
import Input from "@client/components/utils/Input";
import Modal from "@client/components/utils/Modal";
import {
  AsyncModalControls,
  ModalControls,
  useAsyncModalControls,
  useModalControls,
} from "@client/components/utils/useModalControls";
import { RefetchUserProfilesContext } from "@client/pages/round/Round";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  getActiveEvaluators,
  getActiveMembers,
  userIsMeanRecommender,
} from "@engine/round/accessors";
import type { RoundMember } from "@engine/round/aggregate";
import { type Track, trackNames } from "@lib/types";
import { generateEvaluatorId } from "@lib/utils/idGeneration";
import clsx from "clsx";
import { emailExistsInRound } from "@client/utils/emailExistsInRound";

async function getOrCreateUserId(email: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (typeof window === "undefined") {
    headers["Cookie"] =
      `ADD_OR_CREATE_USER_ID_SECRET=${import.meta.env.ADD_OR_CREATE_USER_ID_SECRET}`;
  }

  const res = await fetch(`/api/getOrCreateUserId?email=${email}`, {
    headers,
  });

  const { userId } = await res.json();

  return userId;
}

const enableTrackSelection = true;

const tracks: Track[] = ["MAIN", "FREEDOM", "FAIRNESS", "NO_TRACK"];

export function SettingsUsersPage() {
  const ctx = useContext(StateContext);
  const { round, userProfiles, actingAsUserId: userId } = ctx;
  const isAdmin = userIsAdmin(userId, ctx);
  const dispatch = useContext(DispatchContext);

  const addUserModalControls = useModalControls();
  const addEvaluatorModalControls = useAsyncModalControls<boolean>();
  const removeUserWarningModalControls = useAsyncModalControls<boolean>();
  const removeEvaluatorWarningModalControls = useAsyncModalControls<boolean>();
  const changeRaterRoleModalControls = useAsyncModalControls<boolean>();

  async function removeMember(member: RoundMember) {
    const res = await removeUserWarningModalControls.openModal();

    if (!res) {
      return;
    }

    await dispatch({
      type: "REMOVE_ROUND_MEMBER",
      userId: member.id,
    });
  }

  async function toggleUserRoundAdmin(member: RoundMember) {
    await dispatch({
      type: "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY",
      memberId: member.id,
      admin: !member.isRoundAdmin,
    });
  }

  async function addEvaluator(
    member: RoundMember,
    evaluatorType: "recommender" | "funder",
    shortname: string,
    meanRecommenderThreshold?: number,
  ) {
    try {
      await dispatch({
        type: "ADD_EVALUATOR",
        memberId: member.id,
        evaluatorType: evaluatorType,
        shortname: shortname,
        evaluatorId: generateEvaluatorId(evaluatorType),
        threshold: meanRecommenderThreshold,
      });
    } catch (e) {
      console.error("Error in addEvaluator:", e);
      throw e;
    }
  }

  async function removeEvaluator(evaluatorId: string) {
    const res = await removeEvaluatorWarningModalControls.openModal();

    if (!res) {
      return;
    }

    await dispatch({
      type: "REMOVE_EVALUATOR",
      evaluatorId,
    });
  }

  // funderId is the same as evaluatorId
  async function setFunderBudget(funderId: string, budget: number) {
    await dispatch({
      type: "FUNDER_BUDGET_CHANGE",
      funderId,
      budget,
    });
  }

  async function changeUserInitials(member: RoundMember, newInitials: string) {
    await dispatch({
      type: "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY",
      memberId: member.id,
      initials: newInitials,
    });
  }

  async function changeEvaluatorShortname(
    evaluatorId: string,
    newShortname: string,
  ) {
    await dispatch({
      type: "EVALUATOR_PROPERTY_CHANGE",
      evaluatorId,
      property: "shortname",
      value: newShortname,
    });
  }

  async function changeUserTrack(member: RoundMember, newTrack: Track) {
    await dispatch({
      type: "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY",
      memberId: member.id,
      track: newTrack,
    });
  }

  const activeMembers = getActiveMembers(round);
  const activeEvaluators = getActiveEvaluators(round);

  const numRoundAdmins = activeMembers.filter(
    (member) => member.isRoundAdmin,
  ).length;

  if (!isAdmin) {
    return <div>You must be an admin to edit users.</div>;
  }

  return (
    <div className="w-full max-w-5xl">
      <RoundTitle />
      <div className="mt-4">Users</div>
      <table className="mt-2 border border-stone-700 dark:border-white w-full">
        <thead>
          <tr className="text-xs border-b border-stone-700 dark:border-white">
            <th className="py-2 px-1">Name</th>
            <th className="py-2 px-1">Email</th>
            <th className="py-2 px-1">Initials</th>
            <th className="py-2 px-1">Round Admin</th>
            <th className="py-2 px-1">Track</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {activeMembers.map((member, idx: number) => {
            return (
              <tr key={idx}>
                <td className="p-1">{userProfiles[member.id]?.name}</td>
                <td className="p-1">{userProfiles[member.id]?.email}</td>
                <td className="p-1">
                  <BlurringTextInput
                    value={member.initials}
                    onBlur={(newValue) => changeUserInitials(member, newValue)}
                  />
                </td>
                <td className="p-1">
                  <input
                    disabled={
                      (numRoundAdmins === 1 && member.isRoundAdmin) ||
                      userIsMeanRecommender(member.userId)
                    }
                    type="checkbox"
                    checked={member.isRoundAdmin}
                    onChange={() => toggleUserRoundAdmin(member)}
                  />
                </td>
                <td className="p-1">
                  <TrackSelector
                    track={member.track}
                    setTrack={(newTrack) => changeUserTrack(member, newTrack)}
                    disabled={userIsMeanRecommender(member.userId)}
                  />
                </td>
                <td className="p-1">
                  <Button
                    disabled={numRoundAdmins === 1 && member.isRoundAdmin}
                    onClick={() => removeMember(member)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Button classes="mt-1" onClick={addUserModalControls.openModal}>
        Add user
      </Button>

      <div className="mt-4">Evaluators</div>
      <table className="mt-2 border border-stone-700 dark:border-white w-full">
        <thead>
          <tr className="text-xs border-b border-stone-700 dark:border-white">
            <th className="py-2 px-1">Name</th>
            <th className="py-2 px-1">Short Name</th>
            <th className="py-2 px-1">Type</th>
            <th className="py-2 px-1">Funder Budget</th>
            <th className="py-2 px-1">Mean Rec Threshold</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {activeEvaluators.map((evaluator) => (
            <Fragment key={evaluator.id}>
              <tr className="">
                <td className="p-1">
                  {userProfiles[evaluator.memberId]?.name}
                </td>
                <td className="p-1">
                  <BlurringTextInput
                    value={evaluator.shortname}
                    onBlur={(newValue) =>
                      changeEvaluatorShortname(evaluator.id, newValue)
                    }
                  />
                </td>
                <td className="p-1">{evaluator.type}</td>
                <td className="p-1">
                  {evaluator.type === "funder" && (
                    <BlurringNumberInput
                      value={evaluator.budget}
                      onBlur={(value) => setFunderBudget(evaluator.id, value)}
                      disabled={evaluator.type !== "funder"}
                    />
                  )}
                </td>
                {evaluator.type === "recommender" &&
                userIsMeanRecommender(evaluator.memberId) ? (
                  <td className="p-1">{evaluator.meanRecommenderThreshold}</td>
                ) : (
                  <td />
                )}
                <td className="p-1">
                  <Button
                    disabled={
                      numRoundAdmins === 1 &&
                      round.members[evaluator.memberId]?.isRoundAdmin
                    }
                    onClick={() => removeEvaluator(evaluator.id)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
      <Button classes="my-1" onClick={addEvaluatorModalControls.openModal}>
        Add evaluator
      </Button>

      <AddUserModal modalControls={addUserModalControls} />
      <AddEvaluatorModal
        addEvaluator={addEvaluator}
        modalControls={addEvaluatorModalControls}
      />
      <RemovingUserWarningModal
        modalControls={removeUserWarningModalControls}
      />
      <RemovingEvaluatorWarningModal
        modalControls={removeEvaluatorWarningModalControls}
      />
      <ChangeRaterWarningModal modalControls={changeRaterRoleModalControls} />
      <div className="h-40" />
    </div>
  );
}

function BlurringTextInput({
  value,
  onBlur,
}: {
  value: string;
  onBlur: (value: string) => void;
}) {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    onBlur(localValue);
  };

  return (
    <Input
      type="text"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => handleBlur()}
    />
  );
}

export function BlurringNumberInput({
  value,
  onBlur,
  disabled,
}: {
  value: number;
  onBlur: (value: number) => void;
  disabled?: boolean;
}) {
  const [localValue, setLocalValue] = useState(value);
  const handleBlur = () => {
    onBlur(localValue);
  };
  return (
    <Input
      type="number"
      value={localValue}
      onChange={(e) => setLocalValue(Number(e.target.value))}
      onBlur={() => handleBlur()}
      disabled={disabled}
    />
  );
}

function RemovingUserWarningModal({
  modalControls,
}: {
  modalControls: AsyncModalControls<boolean>;
}) {
  function removeUser() {
    modalControls.closeModal(true);
  }

  function cancel() {
    modalControls.closeModal(false);
  }

  return (
    <Modal isOpen={modalControls.isModalOpen} onClose={cancel}>
      <div className="text-center w-[600px] px-0 py-0">
        <div className="flex relative content-center items-center justify-around mb-4">
          <span className="text-lg">Warning</span>
          <Button onClick={cancel} classes="absolute right-0">
            Close
          </Button>
        </div>

        <div className="flex pb-2">
          <span className="">Are you sure you want to remove this user?</span>
        </div>

        <div className="flex pt-4 justify-around">
          <Button classes="" onClick={removeUser}>
            Remove user
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RemovingEvaluatorWarningModal({
  modalControls,
}: {
  modalControls: AsyncModalControls<boolean>;
}) {
  function removeEvaluator() {
    modalControls.closeModal(true);
  }

  function cancel() {
    modalControls.closeModal(false);
  }

  return (
    <Modal isOpen={modalControls.isModalOpen} onClose={cancel}>
      <div className="text-center w-[600px] px-0 py-0">
        <div className="flex relative content-center items-center justify-around mb-4">
          <span className="text-lg">Warning</span>
          <Button onClick={cancel} classes="absolute right-0">
            Close
          </Button>
        </div>

        <div className="flex pb-2">
          <span className="">
            Are you sure you want to remove this evaluator?
          </span>
        </div>

        <div className="flex pt-4 justify-around">
          <Button onClick={removeEvaluator}>Remove evaluator</Button>
        </div>
      </div>
    </Modal>
  );
}

function ChangeRaterWarningModal({
  modalControls,
}: {
  modalControls: AsyncModalControls<boolean>;
}) {
  function changeUser() {
    modalControls.closeModal(true);
  }

  function cancel() {
    modalControls.closeModal(false);
  }

  return (
    <Modal isOpen={modalControls.isModalOpen} onClose={cancel}>
      <div className="text-center w-[600px] px-0 py-0">
        <div className="flex relative content-center items-center justify-around mb-4">
          <span className="text-lg">Warning</span>
          <Button onClick={cancel} classes="absolute right-0">
            Close
          </Button>
        </div>

        <div className="flex pb-2">
          <span className="">
            Are you sure you want to change the role on this user? This will
            deleted any data related to their previous role
          </span>
        </div>

        <div className="flex pt-4 justify-around">
          <Button onClick={changeUser}>Change Role</Button>
        </div>
      </div>
    </Modal>
  );
}

function AddEvaluatorModal({
  addEvaluator,
  modalControls,
}: {
  addEvaluator: (
    user: RoundMember,
    evaluatorType: "recommender" | "funder",
    shortname: string,
    meanRecommenderThreshold?: number,
  ) => void;
  modalControls: AsyncModalControls<boolean>;
}) {
  const { round, userProfiles } = useContext(StateContext);
  const members = getActiveMembers(round);

  const [member, setMember] = useState<RoundMember>(members[0]);
  const [evaluatorType, setEvaluatorType] = useState<"recommender" | "funder">(
    "recommender",
  );
  const [shortname, setShortname] = useState("");
  const [meanRecommenderThreshold, setMeanRecommenderThreshold] =
    useState<number>(1);

  const [error, setError] = useState<string | null>(null);

  async function changeUser() {
    try {
      addEvaluator(
        member,
        evaluatorType,
        shortname,
        userIsMeanRecommender(member.userId)
          ? meanRecommenderThreshold
          : undefined,
      );
      modalControls.closeModal(true);
    } catch (e) {
      console.log("Error adding evaluator:", e);
      setError(`Error adding evaluator: ${e}`);
    }
  }

  function cancel() {
    modalControls.closeModal(false);
  }

  return (
    <Modal isOpen={modalControls.isModalOpen} onClose={cancel}>
      <form onSubmit={changeUser}>
        <div className="text-center w-[600px] px-0 py-0">
          <div className="flex relative content-center items-center justify-around mb-4">
            <span className="text-lg">Add Evaluator</span>
            <Button type="button" onClick={cancel} classes="absolute right-0">
              Close
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 max-w-sm mx-auto">
            <label htmlFor="user">User:</label>
            <select
              id="user"
              className="text-black border border-[#444] dark:border-none rounded dark:text-white"
              value={member.id}
              onChange={(e) => {
                const selectedUser = members.find(
                  (u) => u.id === e.target.value,
                );
                if (!selectedUser) {
                  return;
                }
                setMember(selectedUser);
              }}
            >
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.initials} {userProfiles[member.id] && "-"}{" "}
                  {userProfiles[member.id]?.name}
                </option>
              ))}
            </select>

            <label htmlFor="evaluatorType">Evaluator Type:</label>
            <select
              id="evaluatorType"
              className="text-black border border-[#444] dark:border-none rounded dark:text-white"
              value={evaluatorType}
              onChange={(e) =>
                setEvaluatorType(e.target.value as "recommender" | "funder")
              }
            >
              <option value="recommender">Recommender</option>
              <option value="funder">Funder</option>
            </select>

            <label htmlFor="shortName">Short Name:</label>
            <Input
              id="shortName"
              type="text"
              value={shortname}
              onChange={(e) => setShortname(e.target.value)}
            />

            {userIsMeanRecommender(member.userId) && (
              <>
                <label htmlFor="meanRecommenderThreshold">
                  Evaluations Threshold:
                </label>
                <BlurringNumberInput
                  value={meanRecommenderThreshold}
                  onBlur={(value) => setMeanRecommenderThreshold(value)}
                />
              </>
            )}
          </div>
          {error && (
            <div className="text-red-500 max-w-sm mx-auto">{error}</div>
          )}
          <div className="flex pt-4 justify-around">
            <Button type="submit" onClick={changeUser}>
              Add
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function AddUserModal({ modalControls }: { modalControls: ModalControls }) {
  const dispatch = useContext(DispatchContext);
  const refetchUserProfiles = useContext(RefetchUserProfilesContext);
  const { round, userProfiles } = useContext(StateContext);

  const [email, setEmail] = useState("");
  const [initials, setInitials] = useState("");
  const [track, setTrack] = useState<Track>("MAIN");

  const emailInUse = emailExistsInRound(email, round, userProfiles);

  async function sendInvite() {
    if (emailInUse) {
      return;
    }
    const userId = await getOrCreateUserId(email);
    await dispatch({
      type: "ADD_ROUND_MEMBER",
      userId,
      initials,
      track,
    });

    refetchUserProfiles?.();
    setEmail("");
    setInitials("");
    setTrack("MAIN");
  }

  const buttonEnabled = email !== "" && initials !== "" && !emailInUse;

  return (
    <Modal
      isOpen={modalControls.isModalOpen}
      onClose={modalControls.closeModal}
    >
      <div className="text-center w-[600px] px-0 py-0">
        <div className="flex relative content-center items-center justify-around mb-4">
          <span className="text-lg">Invite User</span>
          <Button onClick={modalControls.closeModal} classes="absolute right-0">
            Close
          </Button>
        </div>

        <div className="flex pb-2">
          <span className="w-24 text-right pr-2">Email:</span>
          <Input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            classes="w-[300px]"
          />
        </div>

        <div className="flex pb-2">
          <span className="w-24 text-right pr-2">Initials:</span>
          <Input
            type="text"
            value={initials}
            onChange={(e) => setInitials(e.target.value)}
            classes="w-[300px]"
          />
        </div>

        {enableTrackSelection && (
          <div className="flex pb-2">
            <span className="w-24 text-right pr-2">Track:</span>
            <TrackSelector track={track} setTrack={setTrack} />
          </div>
        )}

        {emailInUse && (
          <div className="text-red-500">
            A user with that email already exists.
          </div>
        )}

        <div className="flex pt-4 justify-around">
          <Button onClick={sendInvite} disabled={!buttonEnabled}>
            Add user to database
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TrackSelector({
  track,
  setTrack,
  disabled,
}: {
  track: Track;
  setTrack: (track: Track) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={track}
      onChange={(e) => setTrack(e.target.value as Track)}
      disabled={disabled}
      className={clsx(disabled && "opacity-50")}
    >
      {tracks.map((track) => (
        <option key={track} value={track}>
          {trackNames[track]}
        </option>
      ))}
    </select>
  );
}
